import { useEffect, useRef, useState } from 'react';

export const useCountUp = (end, duration = 2000) => {
  const [count, setCount] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          setIsInView(true);
          hasAnimated.current = true;
        }
      },
      { threshold: 0.3 }
    );

    const currentRef = ref.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  useEffect(() => {
    if (!isInView) return;
    
    let startTime = null;
    const startValue = 0;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = Math.floor(startValue + (end - startValue) * easeOutQuart);
      
      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return { count, ref };
};

export const Counter = ({ end, duration = 2500, suffix = '', className = '' }) => {
  const { count, ref } = useCountUp(end, duration);
  const digits = String(count).split('');
  
  return (
    <span ref={ref} className={`inline-flex items-baseline ${className}`}>
      {digits.map((digit, i) => (
        <span
          key={`${i}-${digit}`}
          className="inline-block"
          style={{
            animation: `digitSlideIn 0.3s ease-out`,
          }}
        >
          {digit}
        </span>
      ))}
      {suffix && <span>{suffix}</span>}
    </span>
  );
};

export default useCountUp;
